import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, AlertTriangle, ChevronRight, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { usePageTitle } from "@/lib/seo";
import { useT } from "@/lib/i18n";

const money = (cents) => {
  if (!cents) return "$0";
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(cents / 100);
};

const SEVERITY = {
  info:     { bg: "bg-sky-50 border-sky-200",         dot: "bg-sky-500",    text: "text-sky-900" },
  warning:  { bg: "bg-amber-50 border-amber-200",     dot: "bg-amber-500",  text: "text-amber-900" },
  critical: { bg: "bg-rose-50 border-rose-200",       dot: "bg-rose-500",   text: "text-rose-900" },
};

export default function LateRentAutopilot() {
  usePageTitle("Late-Rent Autopilot — Central Rentals Canada");
  const t = useT();
  const [leases, setLeases] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackingId, setAckingId] = useState(null);

  const load = async () => {
    try {
      const [l, q] = await Promise.all([
        api.get("/autopilot/leases"),
        api.get("/autopilot/queue"),
      ]);
      setLeases(l.data.leases || []);
      setQueue(q.data.queue || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load autopilot data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (lease_id, enabled) => {
    try {
      await api.post("/autopilot/toggle", { lease_id, enabled });
      toast.success(enabled ? "Autopilot enabled" : "Autopilot disabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed.");
    }
  };

  const ack = async (item_id) => {
    setAckingId(item_id);
    try {
      await api.post("/autopilot/ack", { item_id });
      toast.success("Marked as done");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ack failed.");
    } finally {
      setAckingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-emerald-600 animate-spin"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="autopilot-page">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Zap className="w-5 h-5 text-emerald-700"/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Landlord smart tool</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight mt-1">Late-Rent Autopilot</h1>
            <p className="mt-2 text-slate-500 text-sm max-w-2xl">Turn on autopilot per lease. Central Rentals sends escalating reminders, drafts your N4 at day 7, and nudges you to file at day 14 &mdash; all with a full LTB-admissible audit trail.</p>
          </div>
        </div>

        {/* Escalation queue */}
        <section className="mt-10" data-testid="autopilot-queue-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">Needs your attention ({queue.length})</h2>
            {queue.length === 0 && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-emerald-600"/> All caught up</span>
            )}
          </div>
          {queue.length === 0 ? (
            <div className="mt-4 border border-dashed border-slate-300 rounded-2xl p-8 text-center bg-white" data-testid="autopilot-empty">
              <p className="text-sm text-slate-500">No escalations pending. Flip a lease below to <span className="font-semibold text-slate-700">On</span> and we&apos;ll start watching it.</p>
              <button
                type="button"
                onClick={() => {
                  const el = document.querySelector('[data-testid="autopilot-leases-section"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid="autopilot-enable-cta"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow"
              >
                <Zap className="w-4 h-4"/> Enable on a lease
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {queue.map((q) => {
                const sty = SEVERITY[q.severity] || SEVERITY.info;
                return (
                  <div key={q.id} className={`rounded-2xl border ${sty.bg} p-4 flex items-start gap-4`} data-testid={`queue-item-${q.step}`}>
                    <span className={`mt-1 w-2.5 h-2.5 rounded-full ${sty.dot} flex-none`}/>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm ${sty.text}`}>{q.label} · {q.tenant_name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{q.summary}</div>
                      <div className="text-xs text-slate-500 mt-2 flex gap-4">
                        <span><span className="font-semibold text-slate-700">{q.days_late} days late</span></span>
                        <span>Period: {q.period}</span>
                        <span>Owed: <span className="font-semibold text-slate-700">{money(q.overdue_amount_cents)}</span></span>
                      </div>
                    </div>
                    <button
                      onClick={() => ack(q.id)}
                      disabled={ackingId === q.id}
                      data-testid={`queue-ack-${q.step}`}
                      className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {ackingId === q.id ? <Loader2 className="w-3 h-3 animate-spin"/> : "Mark done"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Lease list w/ toggle */}
        <section className="mt-10" data-testid="autopilot-leases-section">
          <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{t("autopilot.your_leases", "Your leases")}</h2>
          <p className="text-sm text-slate-500 mt-1">{t("autopilot.toggle_desc", "Toggle autopilot per lease. Off-by-default so nothing runs without your consent.")}</p>
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {leases.length === 0 && (
              <div className="p-6 text-sm text-slate-500 text-center">{t("autopilot.no_leases", "No active leases found. Create a lease first.")}</div>
            )}
            {leases.map((l) => (
              <div key={l.lease_id} className="p-4 flex items-center gap-4" data-testid={`lease-row-${l.lease_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{l.tenant_name || l.tenant_email}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {l.property?.address || l.property?.street || "—"}
                    {l.property?.unit_number ? ` · Unit ${l.property.unit_number}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Monthly rent: <span className="font-medium text-slate-700">{money(l.monthly_rent_cents)}</span></div>
                </div>
                <div className="hidden md:block text-right">
                  {l.days_late > 0 ? (
                    <div className="text-xs inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                      <AlertTriangle className="w-3 h-3"/> {l.days_late}d late · {money(l.overdue_amount_cents)}
                    </div>
                  ) : (
                    <div className="text-xs inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5"/> Current
                    </div>
                  )}
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={l.autopilot_enabled}
                    onChange={(e) => toggle(l.lease_id, e.target.checked)}
                    className="peer sr-only"
                    data-testid={`autopilot-toggle-${l.lease_id}`}
                  />
                  <span className="w-11 h-6 rounded-full bg-slate-200 peer-checked:bg-emerald-600 relative transition-colors">
                    <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" style={{ transform: l.autopilot_enabled ? "translateX(20px)" : "translateX(0)" }}/>
                  </span>
                  <span className="text-xs font-semibold text-slate-700 w-8">{l.autopilot_enabled ? "On" : "Off"}</span>
                </label>
              </div>
            ))}
          </div>
        </section>

        {/* Explainer */}
        <section className="mt-10 bg-white border border-slate-200 rounded-2xl p-6" data-testid="autopilot-explainer">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-500 flex-none mt-0.5"/>
            <div className="text-xs text-slate-600 leading-relaxed">
              <p className="font-semibold text-slate-800">How escalations work</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li><span className="font-semibold">Day 1 late</span> &mdash; friendly SMS/email reminder queued to the tenant.</li>
                <li><span className="font-semibold">Day 3 late</span> &mdash; escalated reminder with a one-click payment link.</li>
                <li><span className="font-semibold">Day 7 late</span> &mdash; an N4 (Ontario) or provincial equivalent is drafted and appears above for your review.</li>
                <li><span className="font-semibold">Day 14 late</span> &mdash; we nudge you to file the notice with the LTB or provincial tribunal.</li>
              </ul>
              <p className="mt-3">Every step is timestamped and stored as LTB-admissible evidence. You always control the &ldquo;file&rdquo; step &mdash; nothing is sent to the tribunal without your action.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
